import { useMutation } from '@tanstack/react-query';
import { omit } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { PATHS } from 'src/constants/paths';
import { type IComponent } from 'src/interfaces';
import { requestCCApi } from 'src/utils';
import { improvedFailedToError } from 'src/utils/improvedFailedToError';
import { lang } from 'src/utils/language';
import { queryKeys } from 'src/utils/queryKeys';

export const useUpdateComponent = () => {
  return useMutation({
    mutationFn: updateComponent,
    onSuccess: async () => {
      await queryClient.refetchQueries(queryKeys.components.invalidateAll());
      await queryClient.refetchQueries(queryKeys.componentTags.invalidateAll());
      await queryClient.refetchQueries(
        queryKeys.componentListing.invalidateAll(),
      );
      await queryClient.refetchQueries({
        queryKey: queryKeys.products.invalidateAll(),
      });
      await queryClient.refetchQueries({
        queryKey: queryKeys.viewAssetsWithComponents.invalidateAll(),
      });
    },
  });
};

const updateComponent = async (
  payload: Partial<IComponent> & { componentAssetsName?: string[] },
) => {
  const componentId = payload.id as number;

  let formattedData = {
    ...omit(
      payload,
      'id',
      'created_at',
      'modified_at',
      'section',
      'component_type',
      'componentAssetsName',
      'layoutType',
      'productCategories',
      'productVariations',
      'componentTags',
      'dayparts',
      'theme',
      'assets',
      'phase',
    ),
  };

  if (payload?.config) {
    formattedData = {
      ...formattedData,
      config: omit(payload.config, 'themeId', 'themeKey', 'isBackupProduct'),
    };
  }

  const data = {
    data: { ...formattedData },
  };

  const response = await requestCCApi(
    {
      url: PATHS.COMPONENTS_ID(componentId),
      method: 'PATCH',
      data: data,
    },
    true,
    improvedFailedToError(lang.update, lang.componentName),
  );

  return response.data;
};

export const useUpdateComponentTags = () => {
  return useMutation({
    mutationFn: updateComponentTags,
    onSuccess: () => {
      queryClient.removeQueries(queryKeys.components.invalidateAll());
      queryClient.removeQueries(queryKeys.componentListing.invalidateAll());
      queryClient.invalidateQueries(queryKeys.componentTags.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.viewComponentTagWithComponentCount.invalidateAll(),
      );
    },
  });
};

const updateComponentTags = async (payload: IComponent[]) => {
  const data = {
    data: payload.map((component) => ({
      resource: 'components',
      method: 'PATCH',
      id: component.id,
      data: {
        ...(component.component_tag_id && {
          component_tag_id: component.component_tag_id,
        }),
      },
    })),
  };

  return requestCCApi({ url: '/transaction', method: 'POST', data });
};
